"use client";

// WO-FOUNDER-WALK-UNBLOCK-001 · T5 — THE MEMBER PREVIEW TOGGLE (§27.A option (a), the ruled option).
//
// The founder asked for a way to sign in and see member state. §25.9 measured why he could not: the
// legacy contract carries NO login, register or oauth endpoint at all — zero hits — so there is no
// member service to sign into in phase 1. §27.A put three honest options in front of him and
// recommended (a): a preview toggle he can flip to see member pricing and wallet surfaces, LABELLED,
// and never on the customer path. Real SSO returns as backend scope (WO-B14).
//
// WHAT THIS IS NOT: it is not a fake session. It signs in through the REAL auth path — the cart
// store's `signIn`, which posts to `/api/auth/login` and gets a real JWT from the local Strapi. So
// the member state he sees is the state a real member gets, and the rule "blank credentials must
// never appear signed in" is untouched: this control supplies real credentials, it does not bypass
// the check. Nothing here writes a session the server did not issue.
//
// WHY IT IS SAFE TO SHIP IN THE BUNDLE. It renders only off a production host. The gate is an
// ALLOW-list of preview hosts, not a deny-list of production ones: a deny-list fails open the day
// someone adds a new production domain, and this is the one control that must never appear on one.
//
// The credentials are the seeded local development account (`snowclub-be` `yarn seed`, recorded in
// the repo's CLAUDE.md §10). They exist only in a local Strapi and are not a secret of any kind —
// but they are still not typed into the page for him, so the field can hold a different account when
// he wants to look at someone else's state.

import { useEffect, useState } from "react";
import { isSignedIn, useCartStore } from "@/lib/cart-store";

/** The seeded local account — see CLAUDE.md §10 "Seeded local logins". */
const PREVIEW_EMAIL = "vendor1.user1@example.com";
const PREVIEW_PASSWORD = "password123";

/** ALLOW-list. A host not named here gets no toggle, and that includes any host we have not thought of. */
function isPreviewHost(host: string): boolean {
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "[::1]" ||
    host.endsWith(".local") ||
    host.endsWith(".localhost")
  );
}

export default function MemberPreviewToggle() {
  const [show, setShow] = useState(false);
  // ── QA-BATCH-01 rows 6/9 follow-on (2026-08-27) · COLLAPSED BY DEFAULT, BOTTOM-LEFT ────────────
  // MEASURED on the repaired shell (crawlable-links-gate, localhost:3025): with the document owning
  // scroll and the one footer at document level, this fixed 236x215 card sat exactly over the
  // footer legal row at full scroll and made Terms / Contact / FAQ un-hittable — the gate read
  // "present for crawlers, NOT visible to a visitor" on 12 routes, on the preview host only (this
  // control never renders off localhost). A preview affordance may not cost the page its legal
  // links, so it opens as a small pill and expands on click; bottom-LEFT, where at full scroll it
  // overlaps the © text (not an anchor) instead of the legal links. Full card, same behaviour,
  // one click away.
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [email, setEmail] = useState(PREVIEW_EMAIL);
  const [password, setPassword] = useState(PREVIEW_PASSWORD);
  const session = useCartStore((s) => s.session);
  const signIn = useCartStore((s) => s.signIn);
  const signOut = useCartStore((s) => s.signOut);

  // Host is only knowable in the browser, and rendering server-side then hiding would put the control
  // in production HTML for one paint. Mount decides.
  useEffect(() => setShow(isPreviewHost(window.location.hostname)), []);
  if (!show) return null;

  const on = isSignedIn(session);

  if (!open) {
    return (
      <button
        type="button"
        data-member-preview-pill=""
        data-member-preview={on ? "on" : "off"}
        onClick={() => setOpen(true)}
        style={pill}
        aria-label="Open the member preview panel"
      >
        <span style={dot(on)} aria-hidden />
        <strong style={{ letterSpacing: "0.08em" }}>MEMBER PREVIEW</strong>
      </button>
    );
  }

  const go = async () => {
    setBusy(true);
    setErr(null);
    const r = await signIn(email.trim(), password);
    setBusy(false);
    // The honest failure. A preview control that silently does nothing is the same defect class as the
    // sign-in box that accepted blank credentials — it just fails in the quieter direction.
    if (!r?.ok) setErr(r?.error || "Sign-in refused — is the local backend running on :1337?");
  };

  return (
    <div data-member-preview={on ? "on" : "off"} style={box}>
      <div style={head}>
        <span style={dot(on)} aria-hidden />
        <strong style={{ letterSpacing: "0.08em" }}>MEMBER PREVIEW</strong>
        <button
          type="button"
          data-member-preview-collapse=""
          onClick={() => setOpen(false)}
          aria-label="Collapse the member preview panel"
          style={{ marginLeft: "auto", background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 2 }}
        >
          ×
        </button>
      </div>
      <p style={note}>
        Preview only — not a customer surface. Signs in with a real account so member fares and member
        surfaces render the way a member sees them.
      </p>
      {on ? (
        <>
          <div data-member-preview-email style={{ fontSize: 12, color: "#1a7f37", margin: "0 0 8px" }}>
            Signed in as {session.email}
          </div>
          <button type="button" data-member-preview-off onClick={() => signOut()} style={btn(false)}>
            Turn off
          </button>
        </>
      ) : (
        <>
          <input
            aria-label="Preview account email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={input}
            autoComplete="off"
          />
          <input
            aria-label="Preview account password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={input}
            autoComplete="off"
          />
          <button type="button" data-member-preview-on disabled={busy} onClick={() => void go()} style={btn(true)}>
            {busy ? "Signing in…" : "Turn on"}
          </button>
        </>
      )}
      {err ? (
        <div data-member-preview-error style={{ color: "#b42318", fontSize: 11, marginTop: 6 }}>
          {err}
        </div>
      ) : null}
    </div>
  );
}

// Bottom-LEFT, both states — see the QA-BATCH-01 note above: at full scroll the left corner holds
// the © text; the right corner holds the footer's legal links, which this must never cover.
const pill: React.CSSProperties = {
  position: "fixed",
  left: 12,
  bottom: 12,
  zIndex: 2147483000,
  display: "flex",
  alignItems: "center",
  gap: 7,
  padding: "7px 12px",
  background: "#111827",
  color: "#f9fafb",
  border: "1px solid #374151",
  borderRadius: 999,
  fontFamily: '"Roboto", system-ui, sans-serif',
  fontSize: 10,
  cursor: "pointer",
  boxShadow: "0 10px 24px -12px rgba(0,0,0,0.6)",
};
const box: React.CSSProperties = {
  position: "fixed",
  left: 12,
  bottom: 12,
  zIndex: 2147483000,
  width: 236,
  padding: "12px 14px",
  background: "#111827",
  color: "#f9fafb",
  border: "1px solid #374151",
  borderRadius: 10,
  fontFamily: '"Roboto", system-ui, sans-serif',
  fontSize: 12,
  boxShadow: "0 16px 34px -14px rgba(0,0,0,0.6)",
};
const head: React.CSSProperties = { display: "flex", alignItems: "center", gap: 7, fontSize: 11, marginBottom: 6 };
const note: React.CSSProperties = { margin: "0 0 10px", fontSize: 11, lineHeight: 1.45, color: "#9ca3af" };
const input: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "6px 8px",
  marginBottom: 6,
  background: "#1f2937",
  border: "1px solid #374151",
  borderRadius: 6,
  color: "#f9fafb",
  fontSize: 12,
};
const btn = (primary: boolean): React.CSSProperties => ({
  width: "100%",
  padding: "7px 10px",
  background: primary ? "#f15a22" : "transparent",
  color: primary ? "#fff" : "#f9fafb",
  border: primary ? "none" : "1px solid #4b5563",
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
});
const dot = (on: boolean): React.CSSProperties => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: on ? "#22c55e" : "#6b7280",
  display: "inline-block",
});
